import React, { useState, useEffect, useRef } from 'react';
import { Bell, MessageCircle, Star, Info, Check, X, Trash2 } from 'lucide-react';
import { supabase } from '@/core/infrastructure/supabase/supabaseClient';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/core/application/security/useAuthStore';
import { useProfileStore } from '@/core/application/state/useProfileStore';

export interface Notification {
  id: string;
  tipo: 'chat' | 'review' | 'sistema' | 'cita_estado';
  titulo: string;
  mensaje: string;
  enlace_accion: string | null;
  leida: boolean;
  created_at: string;
  referencia_id: string | null;
}

type ToastType = 'success' | 'info' | 'error';

// Funciones puras para evitar warnings de anidamiento (SonarQube)
const applyUpdate = (prev: Notification[], updated: Notification) => 
  prev.map(n => n.id === updated.id ? updated : n);

const applyDelete = (prev: Notification[], id: string) => 
  prev.filter(n => n.id !== id);

export function GlobalNotificationBell({ userId }: Readonly<{ userId: string | null }>) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { isClinico } = useAuthStore();

  useEffect(() => {
    if (!userId) return;

    // Load initial
    const fetchNotifs = async () => {
      const { data } = await supabase
        .from('notificaciones_inapp')
        .select('*')
        .eq('usuario_id', userId)
        .order('created_at', { ascending: false })
        .limit(20);
      if (data) setNotifications(data);
    };
    fetchNotifs();

    // Subscribe to changes
    const handlePayload = (payload: any) => {
      if (payload.eventType === 'INSERT') {
        setNotifications(prev => [payload.new as Notification, ...prev].slice(0, 20));
      } else if (payload.eventType === 'UPDATE') {
        setNotifications(prev => applyUpdate(prev, payload.new as Notification));
      } else if (payload.eventType === 'DELETE') {
        setNotifications(prev => applyDelete(prev, payload.old.id));
      }
    };

    const channel = supabase.channel('notificaciones_realtime')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'notificaciones_inapp',
        filter: `usuario_id=eq.${userId}`
      }, handlePayload)
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const unreadCount = notifications.filter(n => !n.leida).length;

  const markAsRead = async (id: string, enlace: string | null) => {
    setIsOpen(false);
    // Optimistic update
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, leida: true } : n));
    
    // DB update
    await supabase.from('notificaciones_inapp').update({ leida: true }).eq('id', id);
    
    if (enlace) {
      // Si el enlace apunta a una ruta de profesional pero el usuario es secretaria,
      // redirigir al panel logístico en su lugar (evita el ClinicianGuard).
      if (enlace.startsWith('/pro') && !isClinico) {
        navigate('/secretaria/dashboard');
      } else {
        navigate(enlace);
      }
    }
  };

  const deleteNotification = async (e: React.MouseEvent, notificacion: Notification) => {
    e.stopPropagation();
    
    // Si es una invitación pendiente y la están borrando (X), la rechazamos implícitamente
    // para no dejar el estado en "pendiente" eternamente en la base de datos.
    if (notificacion.tipo === 'sistema' && notificacion.titulo.includes('Invitación') && notificacion.referencia_id && !notificacion.leida) {
      await handleRechazarInvitacion(e, notificacion, true);
      // El handleRechazarInvitacion ya la marca como leída o la actualiza,
      // pero como el usuario explícitamente hizo clic en "X", la removemos de la UI y de la BD.
    }

    // Optimistic UI delete
    setNotifications(prev => prev.filter(n => n.id !== notificacion.id));
    // DB delete
    await supabase.from('notificaciones_inapp').delete().eq('id', notificacion.id);
  };

  const deleteAllNotifications = async () => {
    if (!globalThis.confirm('¿Estás seguro de que deseas borrar todas tus notificaciones?')) return;
    // Optimistic UI delete
    setNotifications([]);
    // DB delete
    await supabase.from('notificaciones_inapp').delete().eq('usuario_id', userId!);
  };

  const [processingId, setProcessingId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ visible: boolean; message: string; type: ToastType }>({ visible: false, message: '', type: 'info' });

  const showToast = (message: string, type: ToastType = 'info') => {
    setToast({ visible: true, message, type });
    if (type !== 'success') {
      setTimeout(() => setToast(prev => ({ ...prev, visible: false })), 4000);
    }
  };

  const handleAceptarInvitacion = async (e: React.MouseEvent, notificacion: Notification) => {
    e.stopPropagation();
    if (processingId) return;
    setProcessingId(notificacion.id);

    try {
      const isTeam = notificacion.titulo.includes('Equipo');
      
      let error, data;
      if (isTeam) {
        const result = await supabase.rpc('fn_aceptar_invitacion_equipo', {
          p_equipo_uuid: notificacion.referencia_id
        });
        error = result.error;
        data = result.data;
      } else {
        const result = await supabase.rpc('fn_aceptar_invitacion_workspace', {
          p_negocio_uuid: notificacion.referencia_id
        });
        error = result.error;
        data = result.data;
      }

      if (error) throw error;
      if (data && !data.success) throw new Error(data.error || 'No se pudo procesar la invitación');
      
      setNotifications(prev => prev.map(n => n.id === notificacion.id ? { ...n, leida: true } : n));
      await supabase.from('notificaciones_inapp').update({ leida: true }).eq('id', notificacion.id);
      
      const { error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError) throw refreshError;
      
      showToast('¡Invitación aceptada! Activando tu panel...', 'success');
      // Refrescar sesión en el store sin recargar la página completa
      const { data: { session: newSession } } = await supabase.auth.getSession();
      if (newSession) {
        await useAuthStore.getState().setSession(newSession);
        await useProfileStore.getState().fetchProfile(true);
      }
      setIsOpen(false);
      setProcessingId(null);
      setTimeout(() => setToast(prev => ({ ...prev, visible: false })), 3000);

    } catch (error: any) {
      console.error('Error aceptando invitación:', error);
      showToast('Hubo un error aceptando la invitación: ' + error.message, 'error');
      setProcessingId(null);
    }
  };

  const handleRechazarInvitacion = async (e: React.MouseEvent, notificacion: Notification, silent = false) => {
    e.stopPropagation();
    if (processingId) return;
    setProcessingId(notificacion.id);

    try {
      const isTeam = notificacion.titulo.includes('Equipo');
      const rpcName = isTeam ? 'fn_rechazar_invitacion_equipo' : 'fn_rechazar_invitacion_workspace';
      const rpcArgs = isTeam 
        ? { p_equipo_uuid: notificacion.referencia_id } 
        : { p_negocio_uuid: notificacion.referencia_id };

      const { data, error } = await supabase.rpc(rpcName as any, rpcArgs);
      
      if (error) throw error;
      if (data && !data.success) throw new Error(data.error || 'No se pudo procesar la invitación');
      
      // En lugar de marcarla como leída (lo que haría que la UI la muestre como "Aceptada"),
      // simplemente la eliminamos de la pantalla y de la base de datos.
      setNotifications(prev => prev.filter(n => n.id !== notificacion.id));
      await supabase.from('notificaciones_inapp').delete().eq('id', notificacion.id);
    } catch (error: any) {
      console.error('Error rechazando invitación:', error);
      if (!silent) showToast('Hubo un error al rechazar: ' + error.message, 'error');
    } finally {
      setProcessingId(null);
    }
  };

  const getIcon = (tipo: string) => {
    switch (tipo) {
      case 'chat': return <MessageCircle size={16} color="#3B82F6" />;
      case 'review': return <Star size={16} color="#F59E0B" />;
      case 'cita_estado': return <Check size={16} color="#10B981" />;
      default: return <Info size={16} color="#64748B" />;
    }
  };

  if (!userId) return null;

  return (
    <div style={{ position: 'relative' }} ref={dropdownRef}>
      <button 
        onClick={() => setIsOpen(!isOpen)}
        style={{
          backgroundColor: isOpen ? '#1E293B' : 'transparent',
          border: 'none',
          padding: '8px',
          borderRadius: '50%',
          cursor: 'pointer',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background-color 0.2s'
        }}
      >
        <Bell size={20} color={isOpen ? '#FFFFFF' : '#94A3B8'} />
        {unreadCount > 0 && (
          <div style={{
            position: 'absolute',
            top: '0px',
            right: '0px',
            backgroundColor: '#EF4444',
            color: '#FFFFFF',
            fontSize: '10px',
            fontWeight: 800,
            width: '16px',
            height: '16px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid #0F172A',
            animation: 'pulse 2s infinite'
          }}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </div>
        )}
      </button>

      {isOpen && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 12px)',
          right: '0',
          width: '320px',
          backgroundColor: '#1E293B',
          borderRadius: '16px',
          boxShadow: '0 10px 25px rgba(0,0,0,0.5)',
          border: '1px solid #334155',
          overflow: 'hidden',
          zIndex: 9999,
          animation: 'fadeInUp 0.2s ease-out'
        }}>
          <div style={{ padding: '16px', borderBottom: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#FFFFFF' }}>Notificaciones</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {unreadCount > 0 && (
                <span style={{ fontSize: '12px', color: '#94A3B8', fontWeight: 600 }}>{unreadCount} nuevas</span>
              )}
              {notifications.length > 0 && (
                <button 
                  onClick={deleteAllNotifications}
                  title="Borrar todas"
                  style={{ 
                    background: 'transparent', border: 'none', cursor: 'pointer', color: '#64748B', 
                    padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    borderRadius: '4px', transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = '#EF4444'; e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = '#64748B'; e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          </div>
          
          <div style={{ maxHeight: '360px', overflowY: 'auto' }}>
            {notifications.length === 0 ? (
              <div style={{ padding: '40px 16px', textAlign: 'center', color: '#64748B' }}>
                <Bell size={32} color="#334155" style={{ margin: '0 auto 12px auto' }} />
                <p style={{ margin: 0, fontSize: '14px', fontWeight: 600 }}>No tienes notificaciones</p>
                <p style={{ margin: '4px 0 0 0', fontSize: '13px' }}>Aquí aparecerán tus alertas importantes.</p>
              </div>
            ) : (
              notifications.map((n) => (
                <button
                  key={n.id}
                  onClick={() => markAsRead(n.id, n.enlace_accion)}
                  style={{
                    width: '100%',
                    padding: '16px',
                    border: 'none',
                    borderBottom: '1px solid #334155',
                    backgroundColor: n.leida ? 'transparent' : 'rgba(59, 130, 246, 0.05)',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'background-color 0.2s'
                  }}
                >
                  <div style={{ 
                    width: '36px', height: '36px', borderRadius: '18px', 
                    backgroundColor: '#0F172A', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 
                  }}>
                    {getIcon(n.tipo)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                        <span style={{ fontSize: '14px', fontWeight: n.leida ? 600 : 800, color: n.leida ? '#E2E8F0' : '#FFFFFF' }}>
                          {n.titulo}
                        </span>
                        {!n.leida && <div style={{ width: '8px', height: '8px', borderRadius: '4px', backgroundColor: '#3B82F6', flexShrink: 0 }} />}
                      </div>
                      <button
                        onClick={(e) => deleteNotification(e, n)}
                        title="Eliminar notificación"
                        style={{
                          background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px',
                          color: '#64748B', borderRadius: '4px', display: 'flex', flexShrink: 0,
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = '#EF4444'; e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = '#64748B'; e.currentTarget.style.backgroundColor = 'transparent'; }}
                      >
                        <X size={14} />
                      </button>
                    </div>
                    <p style={{ margin: 0, fontSize: '13px', color: '#94A3B8', lineHeight: '1.4', paddingRight: '16px' }}>
                      {n.mensaje}
                    </p>
                    <span style={{ fontSize: '11px', color: '#64748B', marginTop: '6px', display: 'block', fontWeight: 500 }}>
                      {new Date(n.created_at).toLocaleDateString()} • {new Date(n.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                    </span>
                    {n.tipo === 'sistema' && n.titulo.includes('Invitación') && n.referencia_id && (
                      <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                        {n.leida ? (
                          <div style={{
                            flex: 1, padding: '8px', backgroundColor: 'rgba(16, 185, 129, 0.1)', color: '#10B981',
                            border: '1px solid rgba(16, 185, 129, 0.3)', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
                            textAlign: 'center'
                          }}>
                            ✓ Invitación aceptada
                          </div>
                        ) : (
                          <>
                            <button
                              disabled={processingId === n.id}
                              onClick={(e) => handleAceptarInvitacion(e, n)}
                              style={{
                                flex: 1, padding: '8px', backgroundColor: processingId === n.id ? '#94A3B8' : '#3B82F6', color: '#FFF',
                                border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: processingId === n.id ? 'not-allowed' : 'pointer', transition: 'opacity 0.2s'
                              }}
                              onMouseEnter={(e) => { if (processingId !== n.id) e.currentTarget.style.opacity = '0.9'; }}
                              onMouseLeave={(e) => { if (processingId !== n.id) e.currentTarget.style.opacity = '1'; }}
                            >
                              {processingId === n.id ? 'Cargando...' : 'Aceptar'}
                            </button>
                            <button
                              disabled={processingId === n.id}
                              onClick={(e) => handleRechazarInvitacion(e, n)}
                              style={{
                                flex: 1, padding: '8px', backgroundColor: 'transparent', color: '#94A3B8',
                                border: '1px solid #334155', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: processingId === n.id ? 'not-allowed' : 'pointer', transition: 'background-color 0.2s'
                              }}
                              onMouseEnter={(e) => { if (processingId !== n.id) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'; }}
                              onMouseLeave={(e) => { if (processingId !== n.id) e.currentTarget.style.backgroundColor = 'transparent'; }}
                            >
                              Rechazar
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
      {toast.visible && (
        <div style={{
          position: 'fixed', bottom: '24px', right: '24px', zIndex: 99999,
          padding: '12px 20px', borderRadius: '12px',
          backgroundColor: { success: '#059669', error: '#DC2626', info: '#2563EB' }[toast.type as ToastType] || '#2563EB',
          color: 'white', fontWeight: 600, fontSize: '14px',
          boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
          animation: 'fadeInUp 0.3s ease-out'
        }}>
          {toast.message}
        </div>
      )}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
